/* eslint-disable no-console */
import { Link } from 'react-router-dom';
import {
  AmplifyButton,
  AmplifySection,
  AmplifyCodeField,
  AmplifyLabel,
  AmplifyFormField,
} from '@aws-amplify/ui-react';
import { Auth, Hub, I18n } from 'aws-amplify';
import {
  AuthState,
  AuthStateHandler,
  AUTH_STATE_CHANGE_EVENT,
  TOAST_AUTH_ERROR_EVENT,
  UI_AUTH_CHANNEL,
} from '@aws-amplify/ui-components';
import Logo from '../components/Logo';
import { handleSignIn } from '../utils/handleSignIn';

const NO_EMAIL_ADDRESS = 'No email address.';
const NO_CODE = 'No code.';
const CONFIRM_SIGN_UP_FAILED = 'Code validation failed.';

export default function ConfirmSignUp(props: any) {
  let { signInUsername } = props;
  const { signInPassword } = props;
  let code = '';

  const dispatchToastHubEvent = (message: string) => {
    Hub.dispatch(UI_AUTH_CHANNEL, {
      event: TOAST_AUTH_ERROR_EVENT,
      message: I18n.get(message) ?? message ?? '',
    });
  };

  const dispatchAuthStateChangeEvent: AuthStateHandler = (
    nextAuthState: AuthState,
    data?: object,
  ) => {
    Hub.dispatch(UI_AUTH_CHANNEL, {
      event: AUTH_STATE_CHANGE_EVENT,
      message: nextAuthState,
      data,
    });
  };

  const handleEmailChange = (event: Event): void => {
    const { value } = event?.target as any;
    signInUsername = value?.trim();
  };

  const handleCodeChange = (event: Event): void => {
    const { value } = event?.target as any;
    code = value?.trim();
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    if (event) {
      event.preventDefault();
    }
    try {
      // const { email = username, code } = inputs;

      if (!signInUsername) throw new Error(NO_EMAIL_ADDRESS);
      if (!code) throw new Error(NO_CODE);
      const confirmSignUpResult = await Auth.confirmSignUp(signInUsername, code);

      if (!confirmSignUpResult) {
        throw new Error(CONFIRM_SIGN_UP_FAILED);
      }

      if (signInPassword) {
        await handleSignIn(signInUsername, signInPassword, dispatchAuthStateChangeEvent);
      }

      dispatchAuthStateChangeEvent(AuthState.SignIn);
    } catch (err) {
      const error = err as Error;
      dispatchToastHubEvent(error.message);
    }
  };

  // AmplifyFormField is used instead of email so that we can set the name.
  // This prevents a chrome warning about non-unique names.
  return (
    <div slot="confirm-sign-up" className="flex flex-col items-center py-24">
      <Link to="/">
        <Logo className="w-40 mb-8 -mt-4" />
      </Link>
      <form onSubmit={handleSubmit}>
        <AmplifySection>
          <div style={{ margin: '1rem 0 1.5rem 0' }}>
            <h3
              style={{
                color: 'var(--header-color)',
                fontSize: 'var(--header-size)',
                fontWeight: 700,
                marginBottom: '2.25rem',
              }}
            >
              Sign in to your Account
            </h3>
            <AmplifyFormField
              name="confirmsignupemail"
              typeof="email"
              label="Email"
              placeholder="Email Address"
              value={signInUsername}
              required
              handleInputChange={handleEmailChange}
              style={{
                fontSize: 'var(--amplify-text-sm)',
                height: '3.125rem',
                lineHeight: '1.1',
              }}
            />
            <AmplifyCodeField
              typeof="code"
              label="Code"
              placeholder=""
              required
              handleInputChange={handleCodeChange}
              style={{
                fontSize: 'var(--amplify-text-sm)',
                height: '3.125rem',
                lineHeight: '1.1',
              }}
            />
            <div
              style={{
                minWidth: 0,
                fontSize: 'var(--amplify-text-xs)',
              }}
            >
              <AmplifyLabel
                style={{
                  minWidth: 0,
                  fontSize: 'var(--amplify-text-xs)',
                  color: 'var(--amplify-grey)',
                }}
              >
                Lost your code?{' '}
              </AmplifyLabel>
              <AmplifyButton
                type="button"
                style={{
                  minWidth: 0,
                  fontSize: 'var(--amplify-text-xs)',
                }}
                variant="anchor"
                onClick={async (
                  event: React.MouseEvent<HTMLAmplifyButtonElement> | undefined,
                ): Promise<void> => {
                  if (event) {
                    event.preventDefault();
                  }
                  try {
                    if (!signInUsername) throw new Error(NO_EMAIL_ADDRESS);
                    await Auth.resendSignUp(signInUsername);
                    const now = new Date();
                    dispatchToastHubEvent(
                      `${now.toLocaleDateString()} ${now.toLocaleTimeString()}: Code sent to ${signInUsername}.`,
                    );
                  } catch (err) {
                    const error = err as Error;
                    dispatchToastHubEvent(error.message);
                  }
                }}
                data-test="sign-up-sign-in-link"
              >
                Resend Code
              </AmplifyButton>
            </div>
          </div>
          <div>
            <div
              style={{
                alignItems: 'baseline',
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                textAlign: 'left',
              }}
            >
              <span>
                <AmplifyButton
                  style={{
                    minWidth: 0,
                    fontSize: '0.875rem',
                  }}
                  variant="anchor"
                  onClick={() =>
                    Hub.dispatch(UI_AUTH_CHANNEL, {
                      event: AUTH_STATE_CHANGE_EVENT,
                      message: AuthState.SignIn,
                    })
                  }
                  data-test="sign-up-sign-in-link"
                >
                  Back to Sign In
                </AmplifyButton>
              </span>
              <AmplifyButton
                type="submit"
                variant="button"
                style={{ marginLeft: 'auto', textSizeAdjust: '100%' }}
              >
                Confirm
              </AmplifyButton>
            </div>
          </div>
        </AmplifySection>
      </form>
    </div>
  );
}
